import React from 'react';
import classnames from 'classnames';
import { flushSync } from 'react-dom';
import { Link, matchPath, useLocation } from 'react-router-dom';

export type TabBarItemActiveContext = {
  pathname: string;
  search: string;
};

function splitTabLink(link: string): { path: string; query: string } {
  const q = link.indexOf('?');
  if (q === -1) return { path: link, query: '' };
  return { path: link.slice(0, q), query: link.slice(q + 1) };
}

function locationSatisfiesPending(
  pathname: string,
  locSearch: string,
  pendingLink: string
): boolean {
  const { path, query } = splitTabLink(pendingLink);
  const pathOk = pathname === path || pathname.startsWith(`${path}/`);
  if (!pathOk) return false;
  if (!query) return true;
  const pendParams = new URLSearchParams(query);
  const locParams = new URLSearchParams(locSearch.replace(/^\?/, ''));
  let ok = true;
  pendParams.forEach((v, k) => {
    if (locParams.get(k) !== v) ok = false;
  });
  return ok;
}

interface TabBarProps {
  items: {
    id?: string;
    link?: string;
    label?: string;
    icon?: React.ReactNode;
    notifications?: number;
    /** Secondary indicator alongside `notifications`. */
    notificationAccessory?: React.ReactNode;
    /** Unread dot (e.g. Jobs: requests or gigs); takes precedence over numeric `notifications` badge. */
    showNotificationDot?: boolean;
    /** Overrides default link accessible name when set. */
    linkAriaLabel?: string;
    /** Active when pathname equals or starts with one of these (no query). */
    activePathPrefixes?: string[];
    /** Custom active rule (e.g. query-string aware). */
    isActiveWhen?: (ctx: TabBarItemActiveContext) => boolean;
  }[];
  justifyLinks?: 'between' | 'around';
  className?: string;
  sticky?: boolean;
  tag?: string;
  activeBgClassName?: string;
  floating?: boolean;
  /** Mobile: fixed bottom tab bar. Desktop (`lg:`): top bar (sticky or floating). */
  placement?: 'top' | 'bottom';
  /** `light`: white bar, pink top border, gray inactive / pink active. `solid`: pink bar (default). */
  appearance?: 'solid' | 'light';
}

export const TabBar: React.FunctionComponent<React.PropsWithChildren<TabBarProps>> = ({
  items = [],
  className = '',
  sticky,
  activeBgClassName = 'bg-rose-800 bg-opacity-50',
  justifyLinks = 'around',
  floating,
  placement = 'top',
  appearance = 'solid',
}) => {
  const justifyClass = `justify-${justifyLinks}`;
  const isLight = appearance === 'light';
  const location = useLocation();
  const [pendingTo, setPendingTo] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!pendingTo) return;
    if (locationSatisfiesPending(location.pathname, location.search || '', pendingTo)) {
      setPendingTo(null);
    }
  }, [location.pathname, location.search, pendingTo]);

  const routeMatchesPath = React.useCallback(
    (path: string) =>
      matchPath(location.pathname, { path, exact: false, strict: false }) != null,
    [location.pathname]
  );

  const itemIsActive = React.useCallback(
    (item: TabBarProps['items'][0]) => {
      const to = item.link;
      if (!to) return false;
      if (isLight && pendingTo === to) return true;
      if (isLight && pendingTo) return false;
      const pathname = location.pathname;
      const search = location.search || '';
      if (item.isActiveWhen) {
        return item.isActiveWhen({ pathname, search });
      }
      if (item.activePathPrefixes?.length) {
        return item.activePathPrefixes.some(
          (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
        );
      }
      const { path } = splitTabLink(to);
      return routeMatchesPath(path);
    },
    [isLight, pendingTo, location.pathname, location.search, routeMatchesPath]
  );

  const onTabPointerDown = React.useCallback(
    (to: string | undefined) => {
      if (!isLight || !to) return;
      flushSync(() => setPendingTo(to));
    },
    [isLight]
  );

  const desktopInactive = isLight
    ? 'text-gray-500 focus:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
    : 'text-white active:text-white focus:text-white';

  const desktopActive = isLight
    ? 'rounded-full bg-gray-100 text-primary'
    : `${activeBgClassName} rounded-full text-white`;

  const mobileInactive = isLight
    ? 'text-gray-500 focus:text-gray-700 focus-visible:outline-none'
    : 'text-white active:text-white focus:text-white';

  const mobileActive = isLight
    ? 'rounded-lg bg-gray-100 text-primary'
    : `${activeBgClassName} text-white`;

  const renderDesktopLink = (item: TabBarProps['items'][0]) => {
    const active = itemIsActive(item);
    const key = item.id || item.link || '';
    return (
      <div key={key} className="relative z-10 flex items-center">
        <Link
          to={item.link || '/'}
          className={classnames(
            'external relative m-0 block',
            active ? desktopActive : desktopInactive,
            {
              'flex h-6 items-center justify-center px-3': !item.icon,
            }
          )}
          aria-label={item.linkAriaLabel}
          aria-current={active ? 'page' : undefined}
          onPointerDown={() => onTabPointerDown(item.link)}
        >
          {!item.icon && <div className="text-sm leading-3">{item.label}</div>}
          {item.icon}
        </Link>
        {item.notificationAccessory ? (
          <span
            className="pointer-events-none absolute left-0 top-0 z-20 flex h-4 w-4 translate-x-0.5 translate-y-1 items-center justify-center rounded-full bg-white text-primary shadow-sm ring-1 ring-gray-200"
            aria-hidden
          >
            {item.notificationAccessory}
          </span>
        ) : null}
        {item.showNotificationDot ? (
          <span
            className="pointer-events-none absolute right-0 top-0 z-20 h-2.5 w-2.5 translate-x-1 translate-y-1 rounded-full bg-red-500 shadow ring-2 ring-white"
            aria-hidden
          />
        ) : null}
        {!item.showNotificationDot && !!item.notifications && (
          <span className="pointer-events-none absolute right-0 top-0 z-20 flex h-5 translate-x-1 translate-y-1 transform items-center justify-center rounded-full bg-white px-2 text-xs font-semibold text-danger shadow-lg">
            <small>{item.notifications}</small>
          </span>
        )}
      </div>
    );
  };

  const renderMobileBottomLink = (item: TabBarProps['items'][0]) => {
    const active = itemIsActive(item);
    const key = item.id || item.link || '';
    return (
      <div
        key={key}
        className="relative flex min-w-0 flex-1 flex-col items-stretch justify-center"
      >
        <Link
          to={item.link || '/'}
          className={classnames(
            'external m-0 flex min-h-[3rem] flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1 text-center',
            active ? mobileActive : mobileInactive
          )}
          aria-label={item.linkAriaLabel}
          aria-current={active ? 'page' : undefined}
          onPointerDown={() => onTabPointerDown(item.link)}
        >
          <span className="flex shrink-0 items-center justify-center [&_.Img]:scale-90">
            {item.icon}
          </span>
          {item.label && (
            <span
              className={classnames(
                'max-w-full truncate text-[10px] font-medium leading-tight',
                isLight ? 'text-inherit' : 'opacity-90'
              )}
            >
              {item.label}
            </span>
          )}
        </Link>
        {item.notificationAccessory ? (
          <span
            className="pointer-events-none absolute left-1.5 top-0.5 z-20 flex h-4 w-4 items-center justify-center rounded-full bg-white text-primary shadow-sm ring-1 ring-gray-200"
            aria-hidden
          >
            {item.notificationAccessory}
          </span>
        ) : null}
        {item.showNotificationDot ? (
          <span
            className="pointer-events-none absolute right-2 top-0.5 z-20 h-2.5 w-2.5 rounded-full bg-red-500 shadow ring-2 ring-white"
            aria-hidden
          />
        ) : null}
        {!item.showNotificationDot && !!item.notifications && (
          <span className="pointer-events-none absolute right-1 top-0 z-20 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-white px-1 text-[10px] font-semibold text-danger shadow-md ring-1 ring-gray-200">
            <small>{item.notifications}</small>
          </span>
        )}
      </div>
    );
  };

  const desktopBar = (
    <div
      className={classnames(
        'flex h-11 px-2',
        isLight
          ? 'border-t border-primary bg-white shadow-sm'
          : 'bg-primary text-white',
        sticky && 'sticky top-0',
        justifyClass,
        className
      )}
    >
      {items.map(renderDesktopLink)}
    </div>
  );

  const floatingTopBar = (
    <div className="fixed top-0 left-0 z-20 w-full p-2">
      <div
        className={classnames(
          'relative flex h-11 justify-between overflow-hidden rounded-lg px-2 lg:mx-auto lg:w-7/12',
          justifyClass,
          className,
          isLight
            ? 'border border-gray-200 border-t-2 border-t-primary bg-white/95 text-gray-500 shadow-lg backdrop-blur-10'
            : 'text-white backdrop-blur-10'
        )}
      >
        {!isLight && <div className="absolute inset-0 bg-primary opacity-75" />}
        {items.map(renderDesktopLink)}
      </div>
    </div>
  );

  if (placement === 'bottom') {
    return (
      <>
        <div
          className={classnames(
            'fixed bottom-0 left-0 right-0 z-30 flex lg:hidden',
            justifyClass,
            'pb-[max(0.35rem,env(safe-area-inset-bottom))] pt-1',
            isLight
              ? 'border-t border-primary bg-white shadow-[0_-2px_16px_rgba(0,0,0,0.08)]'
              : 'border-t border-white/10 bg-primary shadow-[0_-4px_24px_rgba(0,0,0,0.12)]'
          )}
        >
          {items.map(renderMobileBottomLink)}
        </div>
        <div className="hidden w-full lg:block">{!floating ? desktopBar : floatingTopBar}</div>
      </>
    );
  }

  return (!floating && desktopBar) || floatingTopBar || null;
};
